import { forwardRef, type ReactNode } from 'react';

import { Description } from './Description.js';
import { Footer } from './Footer.js';
import { COMPONENT_NAME } from './terminology-overlay-constants.js';
import { TerminologyOverlayContext } from './TerminologyOverlayContext.js';
import { Trigger, type TerminologyOverlayTriggerProps } from './Trigger.js';
import { useAriaLabelingProps } from '../../core/hooks/useAriaLabelingProps.js';
import { useBehavioralTrackingProps } from '../../core/hooks/useBehavioralTrackingProps.js';
import { useMergeRefs } from '../../core/hooks/useMergeRefs.js';
import { useSafeForwardProps } from '../../core/hooks/useSafeForwardProps.js';
import { useSlots } from '../../core/hooks/useSlots.js';
import type { AriaLabelingProps } from '../../core/types/a11y-props.js';
import type { BehaviorTrackingProps } from '../../core/types/behavior-tracking-props.js';
import type { DataTestId } from '../../core/types/data-props.js';
import type { MaskingProps } from '../../core/types/masking-props.js';
import type { StylingProps } from '../../core/types/styling-props.js';
import { devLog } from '../../core/utils/logging.js';
import { Flex } from '../../layouts/flex/Flex.js';
import { useBreakpoint } from '../../layouts/hooks/useBreakpoint.js';
import { useOverlayWithTrigger } from '../../overlays/hooks/useOverlayWithTrigger.js';
import { Overlay } from '../../overlays/overlay/Overlay.js';

//#region Typings
/**
 * @public
 */
export interface TerminologyOverlayProps
  extends AriaLabelingProps,
    DataTestId,
    MaskingProps,
    StylingProps,
    BehaviorTrackingProps {
  /**
   * Whether the overlay is open by default
   * @defaultValue false
   */
  defaultOpen?: boolean;
  /**
   * The component accepts TerminologyOverlay.Trigger, TerminologyOverlay.Description, and TerminologyOverlay.Footer as children.
   */
  children: ReactNode;
}
//#endregion Typings

const TerminologyOverlayRoot = /* @__PURE__ */ forwardRef<
  HTMLButtonElement,
  TerminologyOverlayProps
>((props, forwardedRef) => {
  const {
    children,
    defaultOpen,
    className: consumerClassName,
    style: consumerStyle,
    'data-testid': dataTestId,
    'data-dtrum-mask': dataDtrumMask,
    'data-dtrum-allow': dataDtrumAllow,
    ...remainingProps
  } = props;
  const [behavioralTrackingProps, restProps] =
    useBehavioralTrackingProps(remainingProps);
  const ariaLabelingProps = useAriaLabelingProps(restProps);
  const forwardProps = useSafeForwardProps(restProps, ariaLabelingProps);
  const smallerScreens = useBreakpoint('(max-width: 480px)');
  const {
    targetRef,
    triggerRef,
    overlayRef,
    overlayState,
    overlayProps,
    overlayTriggerProps,
  } = useOverlayWithTrigger({
    placement: 'bottom-start',
    placementFallbacks: ['bottom-end', 'top-start', 'top-end'],
    offset: 4,
    crossOffset: -16,
    widthStrategy: smallerScreens ? { min: 200 } : { min: 350 },
    defaultOpen,
    shouldCloseOnBlur: true,
  });

  const rootRef = useMergeRefs([forwardedRef, triggerRef, targetRef]);

  const [slots] = useSlots(children, {
    trigger: Trigger,
    description: Description,
    footer: { match: Footer, multiple: true },
  });
  if (slots.trigger === undefined) {
    devLog('Trigger element has not been defined!');
  }

  const { children: triggerChildren, ...triggerSlotProps } =
    slots.trigger?.props ?? {};

  const triggerProps: Partial<TerminologyOverlayTriggerProps> =
    slots.trigger?.props ?? {};

  return (
    <TerminologyOverlayContext.Provider value={overlayTriggerProps}>
      <Trigger
        ref={rootRef}
        className={triggerProps.className}
        style={triggerProps.style}
        data-testid={triggerProps['data-testid']}
        data-dtrum-mask={triggerProps['data-dtrum-mask'] ?? dataDtrumMask}
        data-dtrum-allow={triggerProps['data-dtrum-allow'] ?? dataDtrumAllow}
        {...triggerSlotProps}
      >
        {triggerChildren}
      </Trigger>
      <Overlay
        ref={overlayRef}
        isOpen={overlayState.isOpen}
        overlayProps={overlayProps}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
        {...ariaLabelingProps}
        {...behavioralTrackingProps}
        {...forwardProps}
        data-dt-component={COMPONENT_NAME}
      >
        <Flex paddingY={12} paddingX={16} flexDirection="column">
          {slots.description}
          {slots.footer}
        </Flex>
      </Overlay>
    </TerminologyOverlayContext.Provider>
  );
});

(
  TerminologyOverlayRoot as typeof TerminologyOverlayRoot & {
    displayName: string;
  }
).displayName = COMPONENT_NAME;

/**
 * Use the `TerminologyOverlay` component to define or explain unusual or technical terms in the UI.
 * @public
 */
export const TerminologyOverlay = Object.assign(TerminologyOverlayRoot, {
  Description,
  Trigger,
  Footer,
});
